import React, {
  useState,
  useEffect,
  useRef
} from 'react';
import { useNavigate } from 'react-router-dom';

import { QRCodeCanvas } from 'qrcode.react';
import JsBarcode from 'jsbarcode';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import StatusBadge from './StatusBadge';

const BarcodeRenderer = ({ value }) => {

  const barcodeRef = useRef(null);

  useEffect(() => {

    if (barcodeRef.current && value) {

     JsBarcode(
      barcodeRef.current,
      value,
      {
        format: "CODE128",
        lineColor: "#111827",
        width: 1.8,
        height: 70,
        displayValue: true,
        fontSize: 16,
        margin: 10,
        background: "transparent"
      }
    );
    }

  }, [value]);

  return (
    <svg ref={barcodeRef}></svg>
  );
};
const AssetDetailDrawer = ({
  asset,
  users,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  onEdit
}) => {
const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
 
  useEffect(() => {
    if (asset) {
      setEditData({
        ...asset,
        asset_category_id: asset?.asset_category_id || ''
      });
    }
  }, [asset]);

  if (!asset || !isOpen) return null;

  const ownerOptions = [
    { value: '', label: 'Select Owner' },
    ...(Array.isArray(users) ? users : []).map(user => ({
      value: user.id,
      label: user.name
    }))
  ];

  // ====================================
  // DYNAMIC HISTORY
  // ====================================
  const assetHistory = Array.isArray(asset?.histories) ? asset.histories : [];

  // ====================================
  // FORMAT DATE
  // ====================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // ====================================
  // FORMAT DATE TIME
  // ====================================
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ====================================
  // GENERATE BARCODE DISPLAY
  // ====================================


  // ====================================
  // COPY TO CLIPBOARD
  // ====================================
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSave = async () => {
    await onUpdate(editData);
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 z-50 lg:relative lg:inset-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 lg:hidden" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-card border-l border-border overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Asset Details</h2>
              
              {/* Asset ID and Barcode section */}
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground font-mono">
                    ID: {asset?.asset_id}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => copyToClipboard(asset?.asset_id, 'Asset ID')}
                    title="Copy Asset ID"
                  >
                    <Icon name="Copy" size={14} />
                  </Button>
                </div>
                
                {/* Barcode Display */}
               
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
        

          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">

            <div className="flex items-center gap-2 mb-4">

              <Icon
                name="QrCode"
                size={20}
                className="text-primary"
              />

              <h3 className="text-lg font-semibold">
                Barcode & QR Code
              </h3>

            </div>

            {/* BARCODE */}

            <div className="bg-white rounded-xl border border-border p-5">

              <div className="flex justify-center">

                <BarcodeRenderer
                  value={asset?.barcode || asset?.asset_id}
                />

              </div>

              <p className="text-center text-sm text-muted-foreground mt-3">
                Linear Barcode (Code 128)
              </p>

            </div>

            {/* QR CODE */}

            <div className="bg-white rounded-xl border border-border p-6 mt-6">

              <div className="flex justify-center">

                <QRCodeCanvas
                  value={JSON.stringify({
                    asset_id: asset?.asset_id,
                    type: asset?.category?.name,
                    owner: asset?.owner?.name,
                    location: asset?.location,
                    status: asset?.status
                  })}
                  size={140}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#111827"
                />
              </div>

              <p className="text-center text-sm text-muted-foreground mt-3">
                QR Code (Asset Details)
              </p>

            </div>

            {/* ACTION BUTTONS */}

            <div className="grid grid-cols-2 gap-3 mt-4">

              <Button
                variant="outline"
                size="sm"
                iconName="Copy"
                iconPosition="left"
                onClick={() =>
                  copyToClipboard(
                    asset?.barcode || asset?.asset_id,
                    'Barcode'
                  )
                }
              >
                Copy Code
              </Button>

              <Button
                variant="outline"
                size="sm"
                iconName="Printer"
                iconPosition="left"
                onClick={() => window.print()}
              >
                Print
              </Button>

            </div>

          </div>

          {/* Asset Name */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Asset Name
            </label>
            <p className="mt-1 text-base font-semibold text-foreground">
              {asset?.name || (asset?.brand ? `${asset.brand} ${asset.model || ''}`.trim() : (asset?.model || 'N/A'))}
            </p>
          </div>

          {/* Model & Brand */}
          <div className="grid grid-cols-2 gap-3 bg-muted/40 p-3 rounded-lg border border-border">
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Model
              </label>
              <p className="mt-0.5 text-sm font-semibold text-foreground">
                {asset?.model || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Brand
              </label>
              <p className="mt-0.5 text-sm font-semibold text-foreground">
                {asset?.brand || 'N/A'}
              </p>
            </div>
          </div>

          {/* Asset Type */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Asset Type
            </label>
            <p className="mt-1 text-sm font-medium capitalize">
              {asset?.category?.name || 'N/A'}
            </p>
          </div>

          {/* Owner */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Owner
            </label>
            <div className="mt-1">
              <Select
                options={ownerOptions}
                value={editData?.owner_id || ''}
                onChange={(value) =>
                  setEditData({
                    ...editData,
                    owner_id: value
                  })
                }
              />
            </div>
          </div>

          {/* Serial Number */}
          {asset?.serial_number && (
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Serial Number
              </label>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm font-mono">{asset.serial_number}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => copyToClipboard(asset.serial_number, 'Serial Number')}
                  title="Copy Serial Number"
                >
                  <Icon name="Copy" size={14} />
                </Button>
              </div>
            </div>
          )}

          {/* Brand & Model */}
          {(asset?.brand || asset?.model) && (
            <div className="grid grid-cols-2 gap-4">
              {asset?.brand && (
                <div>
                  <label className="text-xs font-medium uppercase text-muted-foreground">
                    Brand
                  </label>
                  <p className="text-sm mt-1">{asset.brand}</p>
                </div>
              )}
              {asset?.model && (
                <div>
                  <label className="text-xs font-medium uppercase text-muted-foreground">
                    Model
                  </label>
                  <p className="text-sm mt-1">{asset.model}</p>
                </div>
              )}
            </div>
          )}

          {/* Location */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Location
            </label>
            {isEditing ? (
              <input
                className="w-full border rounded-lg p-2 mt-1"
                value={editData?.location || ''}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    location: e.target.value
                  })
                }
              />
            ) : (
              <p className="text-sm mt-1">
                {asset?.location || 'N/A'}
              </p>
            )}
          </div>

          {/* Department */}
          {asset?.department && (
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Department
              </label>
              <p className="text-sm mt-1">{asset.department}</p>
            </div>
          )}

          {/* Software & Licences */}
          {(asset?.other_software || asset?.software_licence?.software || (asset?.software_licences && asset?.software_licences.length > 0)) && (
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground flex items-center justify-between">
                <span>Software & Licences</span>
                {Array.isArray(asset?.software_licences) && asset.software_licences.length > 0 && (
                  <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-800">
                    {asset.software_licences.length} Attached
                  </span>
                )}
              </label>
              
              {Array.isArray(asset?.software_licences) && asset.software_licences.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {asset.software_licences.map((lic, idx) => (
                    <div
                      key={lic.id || idx}
                      className="p-2.5 rounded-lg bg-muted/40 border border-border text-xs space-y-1 hover:border-emerald-500/40 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex items-center gap-1.5 font-semibold text-foreground">
                          <Icon name="ShieldCheck" size={14} className="text-emerald-500" />
                          <span>{lic.software}</span>
                          {lic.version && (
                            <span className="text-[10px] text-muted-foreground font-mono">
                              v{lic.version}
                            </span>
                          )}
                        </div>
                        {lic.licence_type && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300 font-medium">
                            {lic.licence_type}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        {lic.vendor && (
                          <span className="flex items-center gap-1 truncate">
                            <Icon name="Building2" size={11} className="opacity-70" />
                            {lic.vendor}
                          </span>
                        )}
                        {lic.cost > 0 && (
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                            ${Number(lic.cost).toLocaleString()}
                          </span>
                        )}
                      </div>

                      {lic.licence_key && (
                        <div className="text-[10px] font-mono text-muted-foreground/80 truncate pt-0.5 border-t border-border/50">
                          Key: {lic.licence_key}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-1 p-2 rounded-lg bg-muted/40 border border-border text-sm flex items-center gap-2">
                  <Icon name="Layers" size={14} className="text-emerald-500" />
                  <span className="font-medium text-foreground">
                    {asset?.software_licence?.software || asset?.other_software}
                    {asset?.software_licence?.licence_type ? ` (${asset.software_licence.licence_type})` : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Purchase Date */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Purchase Date
            </label>
            <p className="text-sm mt-1">
              {formatDate(asset?.purchase_date)}
            </p>
          </div>

          {/* Warranty */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Warranty Expiration
            </label>
            <p className="text-sm mt-1">
              {formatDate(asset?.warranty_expiration)}
            </p>
          </div>

          {/* Price */}
          {asset?.price && (
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Price
              </label>
              <p className="text-sm mt-1">
                ${parseFloat(asset.price).toLocaleString()}
              </p>
            </div>
          )}

          {/* Status */}
          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">
              Current Status
            </label>
            <div className="mt-2 mb-3">
              <StatusBadge status={asset?.status} />
            </div>
            <Select
              options={[
                { value: 'active', label: 'Active' },
                { value: 'spare', label: 'Spare' },
                { value: 'repair', label: 'Repair' },
                { value: 'retired', label: 'Retired' }
              ]}
              value={asset?.status}
              onChange={async (value) => {
                if (!asset?.id) return;
                await onUpdate({
                  ...asset,
                  status: value
                });
              }}
            />
          </div>

          {/* Description */}
          {asset?.description && (
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Description
              </label>
              <p className="text-sm mt-1 text-muted-foreground">
                {asset.description}
              </p>
            </div>
          )}

          {/* Asset History */}
          <div>
            <h3 className="text-sm font-semibold uppercase mb-4">
              Asset History
            </h3>
            <div className="space-y-4">
              {assetHistory?.length > 0 ? (
                assetHistory.map((event, index) => (
                  <div key={event.id} className="flex space-x-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      {index < assetHistory.length - 1 && (
                        <div className="w-px h-8 bg-border mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <p className="text-sm font-medium">{event.action}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(event.created_at)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        by {event?.user?.name || 'System'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {event.details}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No history found</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          {isEditing && (
            <Button
              variant="default"
              fullWidth
              className="mb-3"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Changes
            </Button>
          )}
          <div className="grid grid-cols-2 gap-2 mb-2">

            <Button
              variant="default"
              size="sm"
              iconName="ExternalLink"
              iconPosition="left"
              onClick={() => {

                if (!asset?.id) {
                  alert('Asset ID missing');
                  return;
                }

                navigate(
                  `/asset-detail-management?id=${asset.id}`
                );
              }}
            >
              Open Page
            </Button>

            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => {
                if (!asset) return;
                onEdit(asset);
              }}
            >
              Edit
            </Button>

          </div>

          <div className="grid grid-cols-2 gap-2">

            <Button
              variant="outline"
              size="sm"
              iconName="MapPin"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Location
            </Button>

            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => onDelete(asset.id)}
            >
              Delete
            </Button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailDrawer;